import StorageIcon from '@mui/icons-material/Storage';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import CriticalActionBanner from '../components/common/CriticalActionBanner';
import KnowledgeDashboard from '../components/KnowledgeBase/KnowledgeDashboard';
import TokenUsageIndicator from '../components/Library/TokenUsageIndicator';
import {
  calculateUsagePercentage,
  FILE_COUNT_LIMITS,
  FILE_SIZE_LIMITS,
  formatBytes,
} from '../config/uploadLimits';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { updateClientProfile } from '../services/adminService';
import { apiService } from '../services/api';
import { DOMAIN_ARCHETYPES } from '../types/knowledge';
import { getUserLibraryStorageUsage, type StorageUsage } from '../utils/storageValidation';
import type { TokenUsageMetrics } from '../utils/tokenUsageUtils';

const LibraryPage = () => {
  const theme = useTheme();
  const { isLoading: isLoadingAuth } = useAuth();
  const { account, hasMissingAccountInfo, refreshUserDetails } = useAccount();
  const { showSnackbar } = useSnackbar();
  const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);
  const [tokenUsage] = useState<TokenUsageMetrics | null>(null);
  const [isBannerDismissed, setIsBannerDismissed] = useState(() => {
    return sessionStorage.getItem('library_critical_banner_dismissed') === 'true';
  });

  const { mode } = useWorkspace();
  const [isSyncingKB, setIsSyncingKB] = useState(false);
  const [syncFrequency, setSyncFrequency] = useState<string>('off');

  useEffect(() => {
    if (account?.llm_settings) {
      const isProcurementEnabled = account?.clientSettings?.enableProcurementModule === true;
      const activeDomains = Object.keys(DOMAIN_ARCHETYPES).filter(
        (domain) => (domain !== 'procurement' && domain !== 'rubrics') || isProcurementEnabled
      ) as (keyof typeof DOMAIN_ARCHETYPES)[];
      const allArchetypes = Array.from(
        new Set(activeDomains.flatMap((domain) => DOMAIN_ARCHETYPES[domain]))
      );
      for (const archetype of allArchetypes) {
        const llmSetting = (
          account.llm_settings as Record<string, { automation?: { frequency?: string } }>
        )?.[archetype];
        if (llmSetting?.automation?.frequency) {
          setSyncFrequency(llmSetting.automation.frequency);
          break;
        }
      }
    }
  }, [account?.llm_settings, account?.clientSettings?.enableProcurementModule]);

  const handleFrequencyChange = async (newFrequency: string) => {
    if (!account?.clientId) {
      return;
    }
    setSyncFrequency(newFrequency);

    const newLlmSettings = JSON.parse(JSON.stringify(account.llm_settings || {}));
    const isProcurementEnabled = account?.clientSettings?.enableProcurementModule === true;
    const activeDomains = Object.keys(DOMAIN_ARCHETYPES).filter(
      (domain) => (domain !== 'procurement' && domain !== 'rubrics') || isProcurementEnabled
    ) as (keyof typeof DOMAIN_ARCHETYPES)[];
    const allArchetypes = Array.from(
      new Set(activeDomains.flatMap((domain) => DOMAIN_ARCHETYPES[domain]))
    );
    let hasChanges = false;

    for (const archetype of allArchetypes) {
      const llmSetting = newLlmSettings[archetype];
      if (llmSetting) {
        if (!llmSetting.automation) {
          llmSetting.automation = { max_usd_limit: 10 };
        }
        if (llmSetting.automation.frequency !== newFrequency) {
          llmSetting.automation.frequency = newFrequency;
          hasChanges = true;
        }
      }
    }

    if (hasChanges) {
      try {
        await updateClientProfile(account.clientId, { llm_settings: newLlmSettings });
        await refreshUserDetails(true);
        showSnackbar('Sync frequency updated successfully.', 'success');
      } catch (_error) {
        showSnackbar('Failed to update sync frequency.', 'error');
      }
    }
  };

  const handleDismissBanner = () => {
    setIsBannerDismissed(true);
    sessionStorage.setItem('library_critical_banner_dismissed', 'true');
  };

  const handleSyncAllKnowledgeBases = async () => {
    if (!account?.uid || !account?.clientId) {
      return;
    }
    setIsSyncingKB(true);
    try {
      // Collect all active archetypes from llm_settings that have target paths or external sites
      const isProcurementEnabled = account?.clientSettings?.enableProcurementModule === true;
      const activeDomains = Object.keys(DOMAIN_ARCHETYPES).filter(
        (domain) => (domain !== 'procurement' && domain !== 'rubrics') || isProcurementEnabled
      ) as (keyof typeof DOMAIN_ARCHETYPES)[];
      const allArchetypes = Array.from(
        new Set(activeDomains.flatMap((domain) => DOMAIN_ARCHETYPES[domain]))
      );
      const archetypesToSync = allArchetypes.filter((archetype) => {
        if (account.storageType !== 'sharepoint') {
          return true; // GCS archetypes are always 'connected'
        }
        // If there's a global sharepoint config, all archetypes are implicitly connected (using default path)
        if (account.sharepointConfig?.siteId && account.sharepointConfig?.driveId) {
          return true;
        }
        const llmSetting = (account.llm_settings as Record<string, unknown>)?.[archetype] as
          | {
              external_site?: { siteId?: string; driveId?: string };
              discovery?: { target_folder_paths?: string[] };
            }
          | undefined;
        return Boolean(
          (llmSetting?.external_site?.siteId && llmSetting?.external_site?.driveId) ||
            (Array.isArray(llmSetting?.discovery?.target_folder_paths) &&
              llmSetting.discovery.target_folder_paths.length > 0)
        );
      });

      if (archetypesToSync.length === 0) {
        showSnackbar('No connected Knowledge Base folders found to sync.', 'info');
        setIsSyncingKB(false);
        return;
      }

      showSnackbar(`Starting sync for ${archetypesToSync.length} connected categories...`, 'info');

      let successCount = 0;
      let failCount = 0;

      for (const archetype of archetypesToSync) {
        try {
          const payload: Parameters<typeof apiService.indexStorage>[0] = {
            userId: account.uid,
            clientId: account.clientId,
            folderPaths: [''],
            mode: archetype,
            collectionType: archetype,
          };

          if (account.storageType === 'sharepoint') {
            const llmSetting = (account.llm_settings as Record<string, unknown>)?.[archetype] as
              | {
                  external_site?: { siteId?: string; driveId?: string };
                  discovery?: { target_folder_paths?: string[] };
                }
              | undefined;
            if (llmSetting?.external_site?.siteId && llmSetting?.external_site?.driveId) {
              payload.siteId = llmSetting.external_site.siteId;
              payload.driveId = llmSetting.external_site.driveId;
            } else {
              payload.siteId = (account.sharepointConfig?.siteId ||
                account.sharepointConfig?.defaultSiteId) as string;
              payload.driveId = (account.sharepointConfig?.driveId ||
                account.sharepointConfig?.defaultDriveId) as string;
            }

            const defaultPath = `clients/${account.clientId}/knowledge_base/${archetype}`;
            payload.folderPaths = [defaultPath];

            if (
              Array.isArray(llmSetting?.discovery?.target_folder_paths) &&
              llmSetting.discovery.target_folder_paths.length > 0
            ) {
              payload.folderPaths.push(...llmSetting.discovery.target_folder_paths);
            }
          }

          const response = await apiService.indexStorage(payload);
          const resData = response.data as { status?: string; message?: string } | undefined;
          if (response.success || resData?.status === 'success') {
            successCount++;
          } else {
            failCount++;
          }
        } catch (_e) {
          failCount++;
        }
      }

      if (failCount === 0) {
        showSnackbar('All Knowledge Bases synchronized successfully!', 'success');
        await refreshUserDetails(true);
      } else if (successCount > 0) {
        showSnackbar(
          `Synchronized ${successCount} categories, but ${failCount} failed.`,
          'warning'
        );
        await refreshUserDetails(true);
      } else {
        showSnackbar('Failed to synchronize all Knowledge Bases.', 'error');
      }
    } catch (_error) {
      showSnackbar('Error during bulk synchronization.', 'error');
    } finally {
      setIsSyncingKB(false);
    }
  };

  const loadStorageUsage = useCallback(
    async (showSuccessMessage = false): Promise<void> => {
      if (!account?.uid || !account?.clientId) {
        return;
      }
      try {
        const usage = await getUserLibraryStorageUsage(account.uid, account.clientId);
        setStorageUsage(usage);
        if (showSuccessMessage) {
          showSnackbar('Storage quota synchronized', 'success');
        }
      } catch {
        showSnackbar('Failed to sync storage usage', 'error');
      }
    },
    [account?.uid, account?.clientId, showSnackbar]
  );

  useEffect(() => {
    loadStorageUsage(false);
  }, [loadStorageUsage]);

  if (isLoadingAuth) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading library data...</Typography>
        </Box>
      </Box>
    );
  }

  if (!account?.clientId || !account.actualDefaultBucketName) {
    let message = 'User not authenticated.';
    if (account && hasMissingAccountInfo) {
      message =
        'User account setup is incomplete. Library features require full account configuration.';
    } else if (account && (!account.clientId || !account.actualDefaultBucketName)) {
      message =
        'Client account details (ID, or Actual Bucket Name) are missing. Cannot display library.';
    }
    return (
      <Box sx={{ width: '100%' }}>
        <Typography color="error">{message}</Typography>
      </Box>
    );
  }

  const storagePercentage = storageUsage
    ? calculateUsagePercentage(storageUsage.totalSize, FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE)
    : 0;
  const fileCountPercentage = storageUsage
    ? calculateUsagePercentage(storageUsage.totalFiles, FILE_COUNT_LIMITS.MAX_LIBRARY_FILES_TOTAL)
    : 0;

  const currentDomain = mode === 'PROCURE' ? 'procurement' : 'bidding';

  return (
    <Box sx={{ width: '100%' }}>
      {tokenUsage && tokenUsage.tokenUsagePercentage > 90 && (
        <CriticalActionBanner
          isOpen={!isBannerDismissed}
          onClose={handleDismissBanner}
          title="Critical Token Usage"
          message="Your library token usage has exceeded 90%. Generative AI features may fail. Please consider removing old or low priority documents to free up space for tender documents and thinking tokens."
        />
      )}

      <Alert severity="info" sx={{ mb: 3, mt: -1, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          Smart Knowledge Base
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Documents uploaded here are automatically indexed and securely stored as context for your
          future bids.
        </Typography>
        <Typography variant="body2">
          <strong>How it works:</strong> Organize your files into specific categories (e.g., CVs,
          Case Studies). The AI will dynamically search the correct category to find the most
          relevant context while drafting each tender section.
        </Typography>
      </Alert>

      <KnowledgeDashboard
        domain={currentDomain}
        sidePanel={
          storageUsage ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: theme.palette.greyLight,
                borderRadius: 3,
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <StorageIcon sx={{ color: theme.palette.bidlogiq.orange, fontSize: '1.2rem' }} />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}
                >
                  Storage Details
                </Typography>
              </Box>

              <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary, fontWeight: 'bold' }}
                  >
                    Storage Type
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}
                  >
                    {account?.storageType === 'sharepoint' ? 'SharePoint' : 'Google Cloud'}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary, fontWeight: 'bold' }}
                  >
                    Status
                  </Typography>
                  <Chip
                    label="Connected"
                    size="small"
                    color="success"
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }}
                  />
                </Box>
              </Box>

              {/* Total Storage Bar */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}
                  >
                    Total Storage
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}
                  >
                    {formatBytes(storageUsage.totalSize)} /{' '}
                    {formatBytes(FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE)} ({storagePercentage}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={storagePercentage}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: theme.palette.greyContrast,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor:
                        storagePercentage >= 90
                          ? theme.palette.error.main
                          : storagePercentage >= 80
                            ? theme.palette.warning.main
                            : theme.palette.bidlogiq.orange,
                    },
                  }}
                />
              </Box>

              {/* Total Files Bar */}
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}
                  >
                    Total Files
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}
                  >
                    {storageUsage.totalFiles} / {FILE_COUNT_LIMITS.MAX_LIBRARY_FILES_TOTAL} (
                    {fileCountPercentage}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={fileCountPercentage}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: theme.palette.greyContrast,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor:
                        fileCountPercentage >= 90
                          ? theme.palette.error.main
                          : fileCountPercentage >= 80
                            ? theme.palette.warning.main
                            : theme.palette.bidlogiq.orange,
                    },
                  }}
                />
              </Box>

              {/* Token Usage Bar */}
              {tokenUsage && <TokenUsageIndicator metrics={tokenUsage} />}

              {/* Warning if approaching limits */}
              {(storagePercentage >= 80 || fileCountPercentage >= 80) && (
                <Alert
                  severity={
                    storagePercentage >= 90 || fileCountPercentage >= 90 ? 'error' : 'warning'
                  }
                  sx={{ mt: 2 }}
                >
                  {storagePercentage >= 90 || fileCountPercentage >= 90
                    ? 'You are approaching your storage or file limits. Please delete some files to free up space.'
                    : 'You are nearing your storage or file limits. Consider managing your files.'}
                </Alert>
              )}

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.05)'
                      : theme.palette.grey[50],
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, textAlign: 'center' }}
                >
                  Choose how often connected folders should automatically sync. Manual sync is
                  always available.
                </Typography>
                <Box
                  sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}
                >
                  <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'background.paper' }}>
                    <InputLabel>Auto-Sync</InputLabel>
                    <Select
                      value={syncFrequency}
                      label="Auto-Sync"
                      onChange={(e) => handleFrequencyChange(e.target.value)}
                      disabled={isSyncingKB}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="nightly">Nightly</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="off">Off</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSyncAllKnowledgeBases}
                    disabled={isSyncingKB}
                    startIcon={
                      isSyncingKB ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />
                    }
                    sx={{
                      whiteSpace: 'nowrap',
                      borderRadius: 1,
                      bgcolor: theme.palette.bidlogiq.brick,
                      color: 'white',
                      '&:hover': {
                        bgcolor: theme.palette.bidlogiq.brick,
                        opacity: 0.9,
                      },
                    }}
                  >
                    {isSyncingKB ? 'Syncing...' : 'Sync All'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          ) : undefined
        }
      />
    </Box>
  );
};

export default LibraryPage;
